/**
 * Model ID Service
 * 
 * Handles mapping between openrouterId and displayedModelID:
 * 
 * - **openrouterId**: Used ONLY for OpenRouter API calls
 *   Example: "google/gemini-2.5-flash-lite-preview-09-2025"
 *   
 * - **displayedModelID**: Used for EVERYTHING stored in Firestore (messages, grades) and UI
 *   Format: "Creator/AAModelName" with optional reasoning suffix
 *   Example: "Google/Gemini 2.5 Flash-Lite Preview (Sep '25) (Reasoning)"
 *   Example: "OpenAI/gpt-oss-120B (high)"
 *   
 * The relationship is NOT always 1-to-1 (e.g., reasoning variants map to same openrouterId)
 * Therefore, both IDs are stored together in messages/grades for reference.
 */

import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { AIModel } from '../types';

/**
 * In-memory cache of model mappings
 * Key: openrouterId (lowercase)
 * Value: Model data with displayedModelID
 */
let modelCache: Map<string, {
  openrouterId: string;
  displayedModelID: string;
  creator: string;
  aaModelName: string;
  defaultReasoningEffort?: 'low' | 'high';
}> | null = null;

let cacheInitialized = false;
let cachePromise: Promise<void> | null = null;

/**
 * Initialize model cache from Firestore
 */
async function initializeCache(): Promise<void> {
  if (cacheInitialized) return;
  
  // If already initializing, wait for that to complete
  if (cachePromise) {
    return cachePromise;
  }
  
  cachePromise = (async () => {
    try {
      console.log('🔄 Initializing model ID cache from Firestore...');
      
      const modelListsRef = doc(db, 'system', 'modelLists');
      const modelListsSnap = await getDoc(modelListsRef);
      
      if (!modelListsSnap.exists()) {
        console.warn('⚠️ system/modelLists not found');
        modelCache = new Map();
        cacheInitialized = true;
        return;
      }
      
      const data = modelListsSnap.data();
      const cache = new Map();
      
      // Process all priority lists
      const lists = ['top', 'speedy', 'balanced', 'cheap', 'free', 'freeUnified', 'balancedFree'];
      
      for (const listName of lists) {
        const listData = data[listName];
        if (!listData?.models || !Array.isArray(listData.models)) continue;
        
        for (const model of listData.models) {
          if (!model.openrouterId || !model.name) continue;
          
          const openrouterId = model.openrouterId;
          const key = openrouterId.toLowerCase();
          
          // Skip if already cached (first occurrence wins)
          if (cache.has(key)) continue;
          
          // Extract creator from openrouterId (lowercase)
          const creator = openrouterId.split('/')[0] || '';
          
          // AA model name from 'name' field (lowercase, keep spaces and special chars)
          const aaModelName = model.name.toLowerCase();
          
          // Build displayedModelID: "creator/aa model name" (all lowercase)
          const displayedModelID = `${creator}/${aaModelName}`;
          
          cache.set(key, {
            openrouterId,
            displayedModelID,
            creator,
            aaModelName,
            defaultReasoningEffort: model.defaultReasoningEffort,
          });
        }
      }
      
      modelCache = cache;
      cacheInitialized = true;
      
      console.log(`✅ Model ID cache initialized with ${cache.size} models`);
    } catch (error) {
      console.error('❌ Failed to initialize model cache:', error);
      modelCache = new Map();
      cacheInitialized = true;
    }
  })();
  
  return cachePromise;
}

/**
 * Get displayedModelID from openrouterId
 * INCLUDES reasoning suffix if reasoningEffort is specified
 * Returns openrouterId if not found in cache
 */
export async function getDisplayedModelID(
  openrouterId: string, 
  reasoningEffort?: 'default' | 'low' | 'high'
): Promise<string> {
  if (!openrouterId) return '';
  
  await initializeCache();
  
  // Don't strip reasoning suffix from input - it might already be displayedModelID format
  const key = openrouterId.toLowerCase();
  
  const cached = modelCache?.get(key);
  if (cached) {
    let displayedId = cached.displayedModelID;
    
    // Add reasoning suffix if model has defaultReasoningEffort and it matches
    if (reasoningEffort && reasoningEffort !== 'default' && 
        cached.defaultReasoningEffort === reasoningEffort) {
      displayedId = `${displayedId} (${reasoningEffort})`;
    }
    
    return displayedId;
  }
  
  // Fallback: Custom models not in system/modelLists
  // Keep openrouterId exactly as-is (already lowercase)
  const cleanId = openrouterId.replace(/\s*\((high|low|medium|default)\)\s*$/, '');
  return cleanId;
}

/**
 * Get creator from openrouterId or displayedModelID (lowercase)
 */
export async function getCreator(modelId: string): Promise<string> {
  if (!modelId) return '';
  
  await initializeCache();
  
  // Strip reasoning suffix
  const cleanId = modelId.replace(/\s*\((high|low|medium|default)\)\s*$/, '');
  const key = cleanId.toLowerCase();
  
  const cached = modelCache?.get(key);
  if (cached) {
    return cached.creator;
  }
  
  // Fallback: Extract from ID (keep lowercase)
  return cleanId.split('/')[0] || '';
}

/**
 * Get AA model name from openrouterId
 */
export async function getAAModelName(openrouterId: string): Promise<string> {
  if (!openrouterId) return '';
  
  await initializeCache();
  
  // Strip reasoning suffix
  const cleanId = openrouterId.replace(/\s*\((high|low|medium|default)\)\s*$/, '');
  const key = cleanId.toLowerCase();
  
  const cached = modelCache?.get(key);
  if (cached) {
    return cached.aaModelName;
  }
  
  // Fallback: Use model part of openrouterId
  const parts = cleanId.split('/');
  return parts.slice(1).join('/');
}


/**
 * Clean displayedModelID for use in Firestore field names
 * Removes spaces, special characters, converts to lowercase
 */
export function cleanDisplayedModelIDForField(displayedModelID: string): string {
  return displayedModelID
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-\/]/g, '');
}

/**
 * Get openrouterId from displayedModelID (strips reasoning suffix first)
 */
export async function getOpenrouterIdFromDisplayed(displayedModelID: string): Promise<string | null> {
  if (!displayedModelID) return null;
  
  await initializeCache();
  
  // Strip reasoning suffix if present
  const baseDisplayedId = displayedModelID.replace(/\s*\((high|low|medium|default)\)\s*$/, '');
  
  // Search cache for matching displayedModelID
  if (modelCache) {
    for (const [key, data] of Array.from(modelCache.entries())) {
      if (data.displayedModelID === baseDisplayedId) {
        return data.openrouterId;
      }
    }
  }
  
  return null;
}

/**
 * Extract reasoning suffix from displayedModelID
 * Returns null if no suffix present
 */
export function extractReasoningSuffix(modelId: string): 'high' | 'low' | null {
  const match = modelId.match(/\s*\((high|low)\)\s*$/);
  return match ? match[1] as 'high' | 'low' : null;
}

/**
 * Strip reasoning effort suffix from displayedModelID
 */
export function stripReasoningSuffix(modelId: string): string {
  return modelId.replace(/\s*\((high|low|medium|default)\)\s*$/, '');
}

export const ModelIdService = {
  getDisplayedModelID,
  getCreator,
  getAAModelName,
  getOpenrouterIdFromDisplayed,
  cleanDisplayedModelIDForField,
  stripReasoningSuffix,
  extractReasoningSuffix,
  initializeCache,
};

